import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';
import * as Yup from 'yup';

import Auth from '../../../api/auth';
import { FormModal } from '../../../components/Formik';
import { regexValidator } from '../../../helpers/regexValidator';
import useApiQuery from '../../../hooks/useApiQuery';

import type { APIHookArg } from '@/types';
import type { FormikFormField } from '@/types/formTypes';
import type { TechnicalLead } from '@/types/user';

interface CreateProjectFormModalProps {
  onSubmit: (data: unknown) => void;
  onCancel: () => void;
}

const CreateProjectFormModal = ({ onSubmit, onCancel }: CreateProjectFormModalProps) => {
  const intl = useIntl();
  const { organizationId } = useParams();

  const [technicalLeads, setTechnicalLeads] = useState<TechnicalLead[] | null>(null);

  const { error: fetchError, makeApiCall: getUsersCall } = useApiQuery(Auth.fetchV2Users);

  const { makeApiCall: createProjectCall } = useApiQuery(
    Auth.createV2Project as unknown as APIHookArg,
    {
      successMessage: 'projectCreation',
    }
  );

  useEffect(() => {
    const getTechnicalLeads = async () => {
      const data = (await getUsersCall({ organizationId, roles: ['TECHNICAL_LEAD'] })) as
        TechnicalLead[] | null;

      if (data) {
        const activatedTechnicalLeads = data.filter(technicalLead => technicalLead.activated);
        setTechnicalLeads(activatedTechnicalLeads);
      }
    };

    getTechnicalLeads();
  }, [getUsersCall, organizationId]);

  const fields = [
    {
      fieldType: 'text',
      name: 'name',
      label: intl.formatMessage({ id: 'projectNameField' }),
    },
    {
      fieldType: 'text',
      name: 'description',
      label: intl.formatMessage({ id: 'description' }),
    },
    {
      fieldType: 'select',
      name: 'technicalLeadId',
      label: intl.formatMessage({ id: 'technicalLead' }),
      menuItems: technicalLeads?.map(({ _id, email }) => ({ value: _id, label: email })),
    },
  ];

  const initialValues = {
    name: '',
    description: '',
    technicalLeadId: null,
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required(intl.formatMessage({ id: 'formRequiredError' }))
      .matches(regexValidator.name, intl.formatMessage({ id: 'slugValidationMessage' })),
    description: Yup.string().required(intl.formatMessage({ id: 'formRequiredError' })),
  });

  const createProject = async (formValues: Record<string, unknown>) => {
    onCancel();

    const data = await createProjectCall({
      ...formValues,
      organizationId,
    });

    if (data) {
      onSubmit(data);
    }
  };

  return (
    <FormModal
      title={intl.formatMessage({ id: 'createNewProject' })}
      fields={fields as FormikFormField[]}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={values => {
        createProject(values);
      }}
      onCancel={onCancel}
      submitLabel={intl.formatMessage({ id: 'create' })}
      error={fetchError}
    />
  );
};

export default CreateProjectFormModal;
